import { useState, useCallback } from 'react'
import { AuthContext } from './context/authContextObject'
import { readAuthToken, writeAuthToken, clearAuthToken } from './lib/authToken'

const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://auth.dev.vammo.com'

function getInitialTokenFromUrlOrSession() {
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('token')

  if (urlToken) {
    const normalizedToken = writeAuthToken(urlToken)
    console.log('[auth-init] URL token detected. Stored token:', normalizedToken)
    window.history.replaceState({}, document.title, window.location.pathname)
    return normalizedToken
  }

  const storedToken = readAuthToken()
  console.log('[auth-init] No URL token. Session token:', storedToken)
  return storedToken
}

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(getInitialTokenFromUrlOrSession)
  const [hasAccess, setHasAccess] = useState(false)
  const authInitialized = true

  const setToken = useCallback((nextToken) => {
    const normalizedToken = writeAuthToken(nextToken)
    setTokenState(normalizedToken)
  }, [])

  const markHasAccess = useCallback(() => {
    setHasAccess(true)
  }, [])

  const markNoAccess = useCallback(() => {
    clearAuthToken()
    setTokenState(null)
    setHasAccess(false)
  }, [])

  const redirectToLogin = useCallback(() => {
    const redirectUrl = `https://${window.location.hostname}${window.location.pathname}`
    window.location.href = `${AUTH_URL}?redirectUrl=${encodeURIComponent(redirectUrl)}`
  }, [])

  const decodeToken = (tokenToDecode) => {
    if (typeof window === 'undefined' || !window.atob) return null

    try {
      const base64 = tokenToDecode
        .split('.')[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
      const padding = (4 - (base64.length % 4)) % 4
      return JSON.parse(atob(base64 + '='.repeat(padding)))
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  const value = {
    token,
    hasAccess,
    authInitialized,
    setToken,
    markHasAccess,
    markNoAccess,
    redirectToLogin,
    decodeToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
